import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { subject } from '@casl/ability';
import { Link } from 'react-router-dom';

import config from '../../../config';

import { useAuth } from '../../../Hooks/UseAuth';
import { usePlan } from '../../../Hooks/UsePlan';
import useTranslation from '../../../Hooks/UseTranslation';

import { TChat, TChatMessage } from '../../../Types/Chat';

import UserAvatar from '../../UserAvatar/UserAvatar';
import ChatAutomaticMessage from '../ChatAutomaticMessage/ChatAutomaticMessage';
import ChatBot from '../../ChatBot/ChatBot';
import ChatMessage from '../ChatMessage/ChatMessage';

import "./ChatContent.scss"

interface Props {
  project?: any;
  chat: TChat;
  messages: TChatMessage[];
  onSendMessage: (message: string) => void;
  disabled: boolean;
}

const ChatContent: React.FC<Props> = ({ chat, messages, onSendMessage, disabled, project }) => {

  const { t } = useTranslation();

  const { userData } = useAuth()
  const { plan, showCantWriteToChatModal } = usePlan()

  const inputRef = React.useRef<HTMLInputElement>(null);

  const [ inputValue, setInputValue ] = useState('');
  const [ canWrite, setCanWrite ] = useState(false);
  const [ activePanel, setActivePanel ] = useState<'search' | 'files' | 'members' | null>(null);
  const [ messageSearch, setMessageSearch ] = useState('');

  const getChatIcon = () => {

    if (chat.type === "project") return <span className={project === userData?.id ? 'icon-mis_proyectos' : 'icon-otros_proyectos'}></span>

    if (chat.type === "team") return <span className="icon-grupo_equipo"></span>

    if (chat.type === "private") return <UserAvatar user={chat.user} />
  }

  const getChatTitle = () => {

    if (chat.type === 'project') {

      return chat.project.name
    } else if (chat.type === 'team') {

      return (
        <p className="mb-0 text-title text-16 flex-grow-1">
          {chat.team.name}
        </p>
      )
    } else if (chat.type === 'private') {

      return (
        <p className="mb-0 text-title text-16 flex-grow-1">
          {chat.user?.name + ' ' + chat.user?.surname}
        </p>
      )
    }

    return null
  }

  const getChatSubtitle = () => {

    if (chat.type === 'project') {

      return (
        <div className="chat-content-subtitle">
          <span className="icon-proyecto"></span> Proyecto
        </div>
      )
    }

    if (chat.type === 'team') {

      return (
        <div className="chat-content-subtitle">
          <span className="icon-proyecto"></span> {chat.project?.name}
        </div>
      )
    }

    return null
  }

  const getChatTypeLabel = () => {

    if (chat.type === 'project') return 'Canal de proyecto'
    if (chat.type === 'team') return 'Canal de equipo'
    if (chat.type === 'private') return 'Chat privado'

    return 'Conversación'
  }

  const togglePanel = (panel: 'search' | 'files' | 'members') => {

    setActivePanel(activePanel === panel ? null : panel)
  }

  const members = useMemo(() => {

    if (!chat) return []

    if (chat.users?.length) return chat.users

    if (chat.team?.users?.length) return chat.team.users

    if (chat.user) return [ chat.user ]

    return []
  }, [ chat ])

  const messageMatches = useMemo(() => {

    const search = messageSearch.trim().toLowerCase()

    if (!search) return []

    return messages.filter((message: TChatMessage) => message.content.toLowerCase().includes(search))
  }, [ messageSearch, messages ])

  const interfaceHeights = useSelector((state: any) => state.InterfaceReducer.heights)

  const frameheight = useMemo(() => {

    const contentBodyMargin = 10

    const height = window.innerHeight -
      interfaceHeights.headerHeight -
      interfaceHeights.submenuHeight -
      interfaceHeights.contentHeaderHeight -
      contentBodyMargin -
      interfaceHeights.contentWrapPaddingTop -
      interfaceHeights.contentWrapPaddingBottom

    return height
  }, [ interfaceHeights ])

  const handleClickDisabled = () => {

    if (!canWrite)
      showCantWriteToChatModal(t('CHAT_CANT_CHAT:Title'))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canWrite) {

      handleClickDisabled()
      return
    }

    if (inputValue.trim() !== '') {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  useEffect(() => {

    if (chat)
      setCanWrite(plan.can('write', subject('Chat', { type: chat.type })))
  }, [ chat ])

  useEffect(() => {

    if (inputRef.current)
      inputRef.current.focus()
  }, [ chat ])

  useEffect(() => {

    setActivePanel(null)
    setMessageSearch('')
  }, [ chat?._id ])

  return (
    <>
      {!chat && !config.chat.pl4nnerBot.enabled && (
        <div className="chat-content-empty">
          <div className="chat-empty-panel">
            <div className="chat-empty-icon">
              <i className="fas fa-fw fa-comments"></i>
            </div>
            <h3>{t('CHAT:SelectAChat')}</h3>
            <p>Elige una conversación, un equipo o un proyecto para continuar colaborando.</p>
            <button type="button">
              <i className="fas fa-fw fa-plus"></i>
              Iniciar conversación
            </button>
          </div>
        </div>
      )}
      {!chat && config.chat.pl4nnerBot.enabled && (
        <div className="chat-content">
          <div className="chat-content-header">
            <div className="chat-content-icon-ia">
              <i className="fas fa-fw fa-robot fa-sm" ></i>
            </div>
            <div className='chat-content-texts'>
              <div className='chat-content-title-ai'>
                {t('CHAT:PlannerIA')}
              </div>
              {/* {getChatSubtitle()} */}
            </div>
          </div>
          <div><ChatBot /></div>
        </div>
      )}
      {chat && (
        <div className="chat-content">
          <div className="chat-content-header">
            <div className="chat-content-heading">
              <div className="chat-content-icon">
                {getChatIcon()}
              </div>
              <div className='chat-content-texts'>
                <div className="chat-content-kicker">
                  {getChatTypeLabel()}
                </div>
                <div className='chat-content-title'>
                  {getChatTitle()}
                </div>
                {getChatSubtitle()}
              </div>
            </div>
            <div className="chat-content-actions">
              {chat.project?._id && (
                <Link className="chat-content-action is-primary" to="/">
                  <i className="fas fa-fw fa-arrow-left"></i>
                  <span>Volver a Proyectos</span>
                </Link>
              )}
              <button type="button" className={`chat-content-action ${activePanel === 'search' ? 'active' : ''}`} onClick={() => togglePanel('search')}>
                <i className="fas fa-fw fa-search"></i>
                <span>Buscar</span>
              </button>
              <button type="button" className={`chat-content-action ${activePanel === 'files' ? 'active' : ''}`} onClick={() => togglePanel('files')}>
                <i className="fas fa-fw fa-paperclip"></i>
                <span>Archivos</span>
              </button>
              <button type="button" className={`chat-content-action ${activePanel === 'members' ? 'active' : ''}`} onClick={() => togglePanel('members')}>
                <i className="fas fa-fw fa-users"></i>
                <span>Miembros</span>
              </button>
            </div>
          </div>
          {activePanel && (
            <div className="chat-context-panel">
              {activePanel === 'search' && (
                <>
                  <div className="chat-context-panel-header">
                    <strong>Buscar en esta conversación</strong>
                    <button type="button" onClick={() => setActivePanel(null)}>
                      <i className="fas fa-fw fa-times"></i>
                    </button>
                  </div>
                  <div className="chat-context-search">
                    <i className="fas fa-fw fa-search"></i>
                    <input
                      type="text"
                      placeholder="Escribe una palabra o frase"
                      value={messageSearch}
                      onChange={(event) => setMessageSearch(event.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="chat-context-results">
                    {!messageSearch && <span>Busca decisiones, restricciones, responsables o acuerdos.</span>}
                    {messageSearch && !messageMatches.length && <span>No encontramos mensajes con ese texto.</span>}
                    {messageMatches.slice(0, 5).map((message: TChatMessage) => (
                      <div key={message._id} className="chat-context-result">
                        <strong>{message.sender.name} {message.sender.surname}</strong>
                        <p>{message.content}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {activePanel === 'files' && (
                <>
                  <div className="chat-context-panel-header">
                    <strong>Archivos</strong>
                    <button type="button" onClick={() => setActivePanel(null)}>
                      <i className="fas fa-fw fa-times"></i>
                    </button>
                  </div>
                  <div className="chat-context-empty-state">
                    <i className="fas fa-fw fa-folder-open"></i>
                    <strong>Sin archivos vinculados todavía</strong>
                    <span>Este panel queda listo para mostrar adjuntos cuando exista integración.</span>
                  </div>
                </>
              )}
              {activePanel === 'members' && (
                <>
                  <div className="chat-context-panel-header">
                    <strong>Miembros</strong>
                    <button type="button" onClick={() => setActivePanel(null)}>
                      <i className="fas fa-fw fa-times"></i>
                    </button>
                  </div>
                  <div className="chat-members-list">
                    {!members.length && (
                      <div className="chat-context-empty-state compact">
                        <i className="fas fa-fw fa-users"></i>
                        <span>No hay miembros disponibles en los datos actuales.</span>
                      </div>
                    )}
                    {members.slice(0, 10).map((member: any) => (
                      <div key={member._id} className="chat-member-item">
                        <UserAvatar user={member} />
                        <span>
                          <strong>{member.name} {member.surname}</strong>
                          <small>Miembro del chat</small>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <div className='scroller chat-messages-scroller' style={{ flex: 1}}>
            <div className="messages-container">
              {messages.map((message, i) => (
                <Fragment key={message._id}>
                  {message.isAutomatic && <ChatAutomaticMessage message={message} />}
                  {!message.isAutomatic && (
                    <ChatMessage
                      message={message}
                      chat={chat}
                      isMine={message.sender._id === userData?.id}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="composer-toolbar">
              <button type="button" title="Adjuntar archivo">
                <i className="fas fa-fw fa-paperclip"></i>
              </button>
              <button type="button" title="Mencionar">
                <i className="fas fa-fw fa-at"></i>
              </button>
              <button type="button" title="Formato">
                <i className="fas fa-fw fa-bold"></i>
              </button>
            </div>
            <div className='input-container'>
              <div className='input-barra'>
                <input
                  ref={inputRef}
                  type="text"
                  autoFocus
                  value={inputValue}
                  placeholder={canWrite ? t('CHAT:MessagePlaceholder') : t('CHAT:CantWriteInThisChat')}
                  onChange={(event) => setInputValue(event.target.value)}
                  onClick={handleClickDisabled}
                  readOnly={disabled || !canWrite}
                />
              </div>
              <button type="submit" disabled={disabled}>
                <i className="fas fa-fw fa-paper-plane"></i>
                <span>Enviar</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatContent;
